import { RECORD_CONSTANTS, ALLOWED_FILE_TYPES } from '../constants';

// Error types for consistent error handling
export const ATTACHMENT_ERROR_TYPES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  QUOTA_ERROR: 'QUOTA_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  GENERIC_ERROR: 'GENERIC_ERROR'
};

// Stable error codes for behavior/contracts
export const ATTACHMENT_ERROR_CODES = {
  EMPTY_FILE: 'attachment/empty-file',
  UNSUPPORTED_TYPE: 'attachment/unsupported-type',
  FILE_TOO_LARGE: 'attachment/file-too-large',
  TOO_MANY_ATTACHMENTS: 'attachment/too-many-attachments',
  DAILY_LIMIT_REACHED: 'attachment/daily-limit-reached',
  STORAGE_QUOTA_EXCEEDED: 'attachment/storage-quota-exceeded',
  NOT_AUTHENTICATED: 'attachment/not-authenticated',
  PERMISSION_DENIED: 'attachment/permission-denied',
  NETWORK_ERROR: 'attachment/network-error',
  UPLOAD_FAILED: 'attachment/upload-failed',
  UNKNOWN_ERROR: 'attachment/unknown-error'
};

export const ATTACHMENT_ERROR_I18N_KEYS = {
  [ATTACHMENT_ERROR_CODES.EMPTY_FILE]: 'records.errors.attachments.empty_file',
  [ATTACHMENT_ERROR_CODES.UNSUPPORTED_TYPE]: 'records.errors.attachments.unsupported_type',
  [ATTACHMENT_ERROR_CODES.FILE_TOO_LARGE]: 'records.errors.attachments.file_too_large',
  [ATTACHMENT_ERROR_CODES.TOO_MANY_ATTACHMENTS]: 'records.errors.attachments.too_many_attachments',
  [ATTACHMENT_ERROR_CODES.DAILY_LIMIT_REACHED]: 'records.errors.attachments.daily_limit_reached',
  [ATTACHMENT_ERROR_CODES.STORAGE_QUOTA_EXCEEDED]: 'records.errors.attachments.storage_quota_exceeded',
  [ATTACHMENT_ERROR_CODES.NOT_AUTHENTICATED]: 'records.errors.attachments.not_authenticated',
  [ATTACHMENT_ERROR_CODES.PERMISSION_DENIED]: 'records.errors.attachments.permission_denied',
  [ATTACHMENT_ERROR_CODES.NETWORK_ERROR]: 'records.errors.attachments.network_error',
  [ATTACHMENT_ERROR_CODES.UPLOAD_FAILED]: 'records.errors.attachments.upload_failed',
  [ATTACHMENT_ERROR_CODES.UNKNOWN_ERROR]: 'records.errors.attachments.unknown_error'
};

const ATTACHMENT_ERROR_TYPE_BY_CODE = {
  [ATTACHMENT_ERROR_CODES.EMPTY_FILE]: ATTACHMENT_ERROR_TYPES.VALIDATION_ERROR,
  [ATTACHMENT_ERROR_CODES.UNSUPPORTED_TYPE]: ATTACHMENT_ERROR_TYPES.VALIDATION_ERROR,
  [ATTACHMENT_ERROR_CODES.FILE_TOO_LARGE]: ATTACHMENT_ERROR_TYPES.VALIDATION_ERROR,
  [ATTACHMENT_ERROR_CODES.TOO_MANY_ATTACHMENTS]: ATTACHMENT_ERROR_TYPES.VALIDATION_ERROR,
  [ATTACHMENT_ERROR_CODES.DAILY_LIMIT_REACHED]: ATTACHMENT_ERROR_TYPES.QUOTA_ERROR,
  [ATTACHMENT_ERROR_CODES.STORAGE_QUOTA_EXCEEDED]: ATTACHMENT_ERROR_TYPES.QUOTA_ERROR,
  [ATTACHMENT_ERROR_CODES.NOT_AUTHENTICATED]: ATTACHMENT_ERROR_TYPES.AUTH_ERROR,
  [ATTACHMENT_ERROR_CODES.PERMISSION_DENIED]: ATTACHMENT_ERROR_TYPES.AUTH_ERROR,
  [ATTACHMENT_ERROR_CODES.NETWORK_ERROR]: ATTACHMENT_ERROR_TYPES.NETWORK_ERROR,
  [ATTACHMENT_ERROR_CODES.UPLOAD_FAILED]: ATTACHMENT_ERROR_TYPES.GENERIC_ERROR,
  [ATTACHMENT_ERROR_CODES.UNKNOWN_ERROR]: ATTACHMENT_ERROR_TYPES.GENERIC_ERROR
};

const MAX_ATTACHMENT_SIZE_MB = RECORD_CONSTANTS.MAX_ATTACHMENT_SIZE / 1024 / 1024;

const ATTACHMENT_ERROR_FALLBACK_MESSAGES = {
  [ATTACHMENT_ERROR_CODES.EMPTY_FILE]: 'File is empty (0 bytes). Please select a valid file.',
  [ATTACHMENT_ERROR_CODES.UNSUPPORTED_TYPE]:
    'Unsupported file type. Allowed types: images (JPEG, PNG, GIF, WebP), videos (MP4, WebM, QuickTime), audio (MP3, WAV, OGG, M4A), documents (PDF, TXT, DOC, DOCX, XLS, XLSX, CSV, JSON)',
  [ATTACHMENT_ERROR_CODES.FILE_TOO_LARGE]: `File too large. Maximum size is ${MAX_ATTACHMENT_SIZE_MB}MB`,
  [ATTACHMENT_ERROR_CODES.TOO_MANY_ATTACHMENTS]:
    `Maximum ${RECORD_CONSTANTS.MAX_ATTACHMENTS} attachments allowed per record`,
  [ATTACHMENT_ERROR_CODES.DAILY_LIMIT_REACHED]:
    'Daily upload limit reached. Please try again tomorrow.',
  [ATTACHMENT_ERROR_CODES.STORAGE_QUOTA_EXCEEDED]:
    'Storage quota exceeded. Please delete some files',
  [ATTACHMENT_ERROR_CODES.NOT_AUTHENTICATED]: 'User not authenticated',
  [ATTACHMENT_ERROR_CODES.PERMISSION_DENIED]:
    "You don't have permission to upload files",
  [ATTACHMENT_ERROR_CODES.NETWORK_ERROR]:
    'Network error. Please check your connection and try again',
  [ATTACHMENT_ERROR_CODES.UPLOAD_FAILED]: 'Failed to upload files',
  [ATTACHMENT_ERROR_CODES.UNKNOWN_ERROR]: 'An unexpected error occurred'
};

const getAttachmentErrorParams = (code) => {
  switch (code) {
    case ATTACHMENT_ERROR_CODES.FILE_TOO_LARGE:
      return { maxSizeMB: MAX_ATTACHMENT_SIZE_MB };
    case ATTACHMENT_ERROR_CODES.TOO_MANY_ATTACHMENTS:
      return { maxAttachments: RECORD_CONSTANTS.MAX_ATTACHMENTS };
    default:
      return {};
  }
};

const toMappedError = (code, message) => {
  const normalizedCode = ATTACHMENT_ERROR_I18N_KEYS[code]
    ? code
    : ATTACHMENT_ERROR_CODES.UNKNOWN_ERROR;
  return {
    type: ATTACHMENT_ERROR_TYPE_BY_CODE[normalizedCode] || ATTACHMENT_ERROR_TYPES.GENERIC_ERROR,
    code: normalizedCode,
    i18nKey: ATTACHMENT_ERROR_I18N_KEYS[normalizedCode]
      || ATTACHMENT_ERROR_I18N_KEYS[ATTACHMENT_ERROR_CODES.UNKNOWN_ERROR],
    message: message
      || ATTACHMENT_ERROR_FALLBACK_MESSAGES[normalizedCode]
      || ATTACHMENT_ERROR_FALLBACK_MESSAGES[ATTACHMENT_ERROR_CODES.UNKNOWN_ERROR],
    params: getAttachmentErrorParams(normalizedCode)
  };
};

export function createAttachmentError(code, options = {}) {
  const mapped = toMappedError(code, options.message);
  const error = new Error(mapped.message);
  error.code = mapped.code;
  error.type = mapped.type;
  error.i18nKey = mapped.i18nKey;
  error.params = mapped.params;
  if (options.cause) {
    error.cause = options.cause;
  }
  return error;
}

/**
 * Get file type category from MIME type
 * @param {string} mimeType - The MIME type to check
 * @returns {string|null} The file type category or null if not allowed
 */
export function getFileType(mimeType) {
  for (const [type, mimes] of Object.entries(ALLOWED_FILE_TYPES)) {
    if (mimes.includes(mimeType)) {
      return type;
    }
  }
  return null;
}

/**
 * Validate a single file
 * @param {File} file - The file to validate
 * @returns {{valid: boolean, error?: string, errorType?: string, fileType?: string}}
 */
export function validateFile(file) {
  // Check for zero-byte files
  if (file.size === 0) {
    const mapped = toMappedError(ATTACHMENT_ERROR_CODES.EMPTY_FILE);
    return {
      valid: false,
      error: mapped.message,
      errorType: mapped.type,
      errorCode: mapped.code,
      i18nKey: mapped.i18nKey,
      params: mapped.params
    };
  }

  const fileType = getFileType(file.type);
  
  if (!fileType) {
    const mapped = toMappedError(ATTACHMENT_ERROR_CODES.UNSUPPORTED_TYPE);
    return {
      valid: false,
      error: mapped.message,
      errorType: mapped.type,
      errorCode: mapped.code,
      i18nKey: mapped.i18nKey,
      params: mapped.params
    };
  }

  if (file.size > RECORD_CONSTANTS.MAX_ATTACHMENT_SIZE) {
    const mapped = toMappedError(ATTACHMENT_ERROR_CODES.FILE_TOO_LARGE);
    return {
      valid: false,
      error: mapped.message,
      errorType: mapped.type,
      errorCode: mapped.code,
      i18nKey: mapped.i18nKey,
      params: mapped.params
    };
  }

  return { valid: true, fileType };
}

/**
 * Validate multiple files
 * @param {FileList|File[]} files - The files to validate
 * @returns {{validFiles: File[], errors: Array}}
 */
export function validateFiles(files) {
  const fileArray = Array.from(files);
  const validFiles = [];
  const errors = [];
  
  fileArray.forEach(file => {
    const validation = validateFile(file);
    if (validation.valid) {
      validFiles.push(file);
    } else {
      errors.push({
        fileName: file.name,
        message: validation.error,
        errorType: validation.errorType,
        errorCode: validation.errorCode,
        i18nKey: validation.i18nKey,
        params: validation.params
      });
    }
  });
  
  return { validFiles, errors };
}

/**
 * Check if adding files would exceed attachment limit
 * @param {number} currentCount - Current number of attachments
 * @param {number} newCount - Number of new attachments to add
 * @returns {{allowed: boolean, error?: string}}
 */
export function checkAttachmentLimit(currentCount, newCount) {
  if (currentCount + newCount > RECORD_CONSTANTS.MAX_ATTACHMENTS) {
    const mapped = toMappedError(ATTACHMENT_ERROR_CODES.TOO_MANY_ATTACHMENTS);
    return {
      allowed: false,
      error: mapped.message,
      errorType: mapped.type,
      errorCode: mapped.code,
      i18nKey: mapped.i18nKey,
      params: mapped.params
    };
  }
  return { allowed: true };
}

const normalizeAttachmentErrorCode = (error) => {
  const code = error?.code || error?.cause?.code || null;
  if (!code) {
    return null;
  }

  // Already normalized attachment code.
  if (ATTACHMENT_ERROR_I18N_KEYS[code]) {
    return code;
  }

  switch (code) {
    case 'storage/unauthorized':
    case 'permission-denied':
      return ATTACHMENT_ERROR_CODES.PERMISSION_DENIED;
    case 'storage/quota-exceeded':
      return ATTACHMENT_ERROR_CODES.STORAGE_QUOTA_EXCEEDED;
    case 'storage/network-request-failed':
    case 'storage/retry-limit-exceeded':
    case 'network-error':
      return ATTACHMENT_ERROR_CODES.NETWORK_ERROR;
    case 'unauthenticated':
      return ATTACHMENT_ERROR_CODES.NOT_AUTHENTICATED;
    case 'storage/canceled':
      return ATTACHMENT_ERROR_CODES.UPLOAD_FAILED;
    default:
      return null;
  }
};

/**
 * Map Firebase storage errors to our error types
 * @param {Error} error - The error object
 * @returns {{type: string, message: string}}
 */
export function mapStorageError(error) {
  const normalizedCode = normalizeAttachmentErrorCode(error);
  if (normalizedCode) {
    return toMappedError(normalizedCode);
  }

  const fallbackMessage = typeof error?.message === 'string' ? error.message : undefined;
  return toMappedError(ATTACHMENT_ERROR_CODES.UNKNOWN_ERROR, fallbackMessage);
}

/**
 * Generate a user-friendly error message from an error object
 * @param {Error} error - The error object
 * @param {Object} options
 * @param {Function} options.t - Optional translation function
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error, { t } = {}) {
  const mappedError = mapStorageError(error);
  if (typeof t === 'function') {
    return t(mappedError.i18nKey, {
      ...mappedError.params,
      defaultValue: mappedError.message
    });
  }
  return mappedError.message;
}
